import { Box, Button, FormControl, Typography, FormGroup, FormControlLabel, RadioGroup, Radio, Checkbox, TextField, InputAdornment, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useState, useEffect, useRef } from 'react';
import { ListingOnboardingStepProps, ListingFormData, PhotoMetadata } from '../../types/listing_onboarding';
import { useMediaQuery } from '@mui/material';
import { listingService } from '../../services/ListingService';

// Styled components
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UploadBox = styled(Box)(({ theme }) => ({
    border: '2px dashed #ccc',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#fafafa',
    cursor: 'pointer',
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}));

const ListingOnboardingStep1: React.FC<ListingOnboardingStepProps> = ({
    formData,
    setFormData,
    setStepValid,
    pendingListingId,
    photos = [],
    setPhotos,
    isLoadingPhotos = false
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    const [isUploading, setIsUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const isMobile = useMediaQuery('(max-width:600px)');

    // Add validation function
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        // Only show security deposit error if field has been touched
        if (touched.security_deposit) {
            if (!formData.security_deposit || formData.security_deposit.trim() === '') {
                newErrors.security_deposit = 'Security deposit is required.';
            } else if (isNaN(Number(formData.security_deposit.trim()))) {
                newErrors.security_deposit = 'Security deposit must be a number.';
            } else if (Number(formData.security_deposit.trim()) < 0) {
                newErrors.security_deposit = 'Security deposit must be a positive number.';
            }
        }

        // Only show platform error if platforms section has been touched
        if (touched.platforms) {
            const hasSelectedPlatform = (
                formData.platforms.zillow ||
                formData.platforms.facebook ||
                formData.platforms.apartments ||
                formData.platforms.craigslist
            );

            if (!hasSelectedPlatform) {
                newErrors.platforms = 'At least one platform must be selected.';
            }
        }

        setErrors(newErrors);
    };

    // Run validation whenever form data or touched state changes
    useEffect(() => {
        validateForm();
    }, [formData, touched]);

    useEffect(() => {
        const hasSelectedPlatform = (
            formData.platforms.zillow ||
            formData.platforms.facebook ||
            formData.platforms.apartments ||
            formData.platforms.craigslist
        );

        const hasPhotos = photos.length > 0;

        const isValid = Boolean(
            formData.security_deposit &&
            formData.security_deposit.trim() !== '' &&
            hasSelectedPlatform &&
            hasPhotos
        );

        setStepValid(isValid);

        console.log('Step 1 Validation status:', {
            hasDeposit: Boolean(formData.security_deposit),
            hasSelectedPlatform,
            hasPhotos: hasPhotos,
        });
    }, [
        photos,
        formData.security_deposit,
        formData.platforms,
        setStepValid
    ]);

    const handlePlatformChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Mark platforms as touched when user interacts with any platform checkbox
        setTouched(prev => ({ ...prev, platforms: true }));

        const updatedData: ListingFormData = {
            ...formData,
            platforms: {
                ...formData.platforms,
                [event.target.name]: event.target.checked
            }
        };
        setFormData(updatedData);
    };

    const handleDepositChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDeposit = event.target.value.toString().trim();

        // Update form data with string value
        setFormData(prevData => ({
            ...prevData,
            security_deposit: newDeposit
        }));

        // Validate the input immediately if field has been touched
        if (touched.security_deposit) {
            const newErrors = { ...errors };

            if (!newDeposit || newDeposit === '') {
                newErrors.security_deposit = 'Security deposit is required.';
            } else if (isNaN(Number(newDeposit))) {
                newErrors.security_deposit = 'Security deposit must be a number.';
            } else if (Number(newDeposit) < 0) {
                newErrors.security_deposit = 'Security deposit must be a positive number.';
            } else {
                delete newErrors.security_deposit; // Clear the error if validation passes
            }

            setErrors(newErrors);
        }
    };

    const handleDepositBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, security_deposit: true }));
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!pendingListingId || !setPhotos) return;

        setTouched(prev => ({ ...prev, photos: true }));
        const files = Array.from(event.target.files || []);
        const validFiles = files.filter(file => file.type.startsWith('image/'));

        setIsUploading(true);
        try {
            // Upload all files in parallel for better performance
            const uploadPromises = validFiles.map(file =>
                listingService.uploadPhoto(pendingListingId, file)
            );

            const photoMetadataResults = await Promise.all(uploadPromises);

            // Add all uploaded photos to state
            setPhotos(prev => [...prev, ...photoMetadataResults]);
        } catch (error) {
            console.error('Error uploading photos:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = async (photoId: string) => {
        if (!pendingListingId || !setPhotos) return;

        try {
            const response = await listingService.deletePendingPhoto(pendingListingId, photoId);

            // Update local state
            setPhotos(prev => prev.filter(p => p.photoId !== photoId));
        } catch (error) {
            console.error('Error removing photo:', error);
        }
    };

    const handleFeaturePhotoSelect = async (photoId: string) => {
        if (!pendingListingId || !setPhotos) return;

        try {
            await listingService.setFeaturedPhoto(pendingListingId, photoId);

            // Update local state
            setPhotos(prev => prev.map(p => ({
                ...p,
                isFeatured: p.photoId === photoId
            })));
        } catch (error) {
            console.error('Error setting featured photo:', error);
        }
    };

    const handleBoxClick = () => inputRef.current?.click();

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        // Create a synthetic event to match the signature expected by handleImageUpload
        const syntheticEvent = {
            target: { files }
        } as React.ChangeEvent<HTMLInputElement>;
        handleImageUpload(syntheticEvent);
    };

    // Show loading state if photos are being loaded
    if (isLoadingPhotos) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography>Loading photos...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FormControl component="fieldset" sx={{ mt: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }} gutterBottom>
                    Property photos
                </Typography>

                {errors.photos && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'error.main',
                            mb: 1,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                        }}
                    >
                        {errors.photos}
                    </Typography>
                )}

                {/* Mobile Upload Box */}
                {isMobile && (
                    <UploadBox
                        onClick={() => setTouched(prev => ({ ...prev, photos: true }))}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: '80%',
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: 'black',
                            },
                        }}
                    >
                        <Box
                            component="input"
                            ref={mobileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => {
                                handleImageUpload(e);
                            }}
                            sx={{
                                display: 'none'
                            }}
                            id="file-input"
                        />
                        <Button
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => mobileInputRef.current?.click()}
                            disabled={isUploading}
                            sx={{
                                backgroundColor: 'black',
                                '&:hover': {
                                    backgroundColor: '#333',
                                },
                                mb: 4,
                                margin: '0 auto',
                                maxWidth: '80%',
                                alignItems: 'center'
                            }}
                        >
                            {isUploading ? 'Uploading.. ⚡' : 'Upload Photos'}
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: '1rem' }}>
                            {isUploading ? 'Will take a sec or two.' : 'Tap to upload'}
                        </Typography>
                    </UploadBox>
                )}

                {/* Desktop Upload Box */}
                {!isMobile && (
                    <UploadBox
                        onClick={() => {
                            setTouched(prev => ({ ...prev, photos: true }));
                            handleBoxClick();
                            console.log("Desktop Upload Box clicked");
                        }}
                        onDragOver={e => e.preventDefault()}
                        onDragEnter={e => setIsDragging(true)}
                        onDragLeave={e => setIsDragging(false)}
                        onDrop={handleDrop}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: '80%',
                            border: isDragging ? '2px solid #000' : '1px solid #ccc',
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: 'black',
                            },
                        }}
                    >
                        <Button
                            component="label"
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            sx={{
                                backgroundColor: 'black',
                                '&:hover': {
                                    backgroundColor: '#333',
                                },
                                mb: 4,
                                margin: '0 auto',
                                maxWidth: '80%',
                                alignItems: 'center'
                            }}
                        >
                            {isUploading ? 'Uploading.. ⚡' : 'Upload Photos'}
                            <VisuallyHiddenInput
                                ref={inputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => {
                                    console.log("Desktop Button is clicked");
                                    handleImageUpload(e);
                                }}
                                style={{ display: 'none' }}
                            />
                        </Button>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: '1rem' }}>
                            {isUploading ? 'Will take a sec or two.' : 'Drag or click to upload'}
                        </Typography>
                    </UploadBox>
                )}
                {isUploading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '20px', mt: '2rem', margin: '0 auto', maxWidth: '100%' }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Image previews */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3.25,
                    mt: 2,
                    mb: 3
                }}>
                    {photos.map((photo) => (
                        <Box
                            key={photo.photoId}
                            sx={{
                                position: 'relative',
                                width: 100,
                                height: 100,
                            }}
                        >
                            <Box
                                component="img"
                                src={photo.previewUrl}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                }}
                            />
                            {/* Feature Photo Star */}
                            <IconButton
                                size="small"
                                onClick={() => handleFeaturePhotoSelect(photo.photoId)}
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    left: -8,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    },
                                }}
                            >
                                {photo.isFeatured ? (
                                    <StarIcon sx={{ fontSize: 18, color: '#FFD700' }} />
                                ) : (
                                    <StarBorderIcon sx={{ fontSize: 18 }} />
                                )}
                            </IconButton>
                            {/* Remove Button */}
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveImage(photo.photoId)}
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
                {photos.length > 0 && (
                    <Box sx={{ font: 'Inter, sans-serif', mt: '0.5', mb:'0.5', lineHeight: '1.2', color: 'text.secondary'}}>
                        Set a feature photo by selecting a star.
                    </Box>
                )}
                <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                    Security Deposit
                </Typography>
                <TextField
                    id="security_deposit"
                    name="security_deposit"
                    variant="outlined"
                    type="text"
                    value={formData.security_deposit}
                    onChange={handleDepositChange}
                    onBlur={handleDepositBlur}
                    error={!!errors.security_deposit}
                    helperText={errors.security_deposit}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    FormHelperTextProps={{
                        sx: {
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                        }
                    }}
                />

                <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                    Would you like us to list your property on these platforms?
                </Typography>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.platforms.zillow}
                                onChange={handlePlatformChange}
                                name="zillow"
                                sx={{
                                    color: 'grey.500',
                                    '&.Mui-checked': {
                                        color: 'black',
                                    },
                                }}
                            />
                        }
                        label="Zillow"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.platforms.facebook}
                                onChange={handlePlatformChange}
                                name="facebook"
                                sx={{
                                    color: 'grey.500',
                                    '&.Mui-checked': {
                                        color: 'black',
                                    },
                                }}
                            />
                        }
                        label="Facebook Marketplace"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.platforms.apartments}
                                onChange={handlePlatformChange}
                                name="apartments"
                                sx={{
                                    color: 'grey.500',
                                    '&.Mui-checked': {
                                        color: 'black',
                                    },
                                }}
                            />
                        }
                        label="Apartments.com"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.platforms.craigslist}
                                onChange={handlePlatformChange}
                                name="craigslist"
                                sx={{
                                    color: 'grey.500',
                                    '&.Mui-checked': {
                                        color: 'black',
                                    },
                                }}
                            />
                        }
                        label="Craigslist"
                    />
                </FormGroup>
                {errors.platforms && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'error.main',
                            mb: 1,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                        }}
                    >
                        {errors.platforms}
                    </Typography>
                )}
            </FormControl>
        </Box>
    );
};

export default ListingOnboardingStep1;